import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, HeartPulse, MapPin, Star, ShieldCheck, Clock, CheckCircle2, Phone, MessageCircle } from 'lucide-react';
import { professionals } from '../../data/mockData';
import { useAppContext } from '../../contexts/AppContext';
export function FindNurseFlow() {
  const navigate = useNavigate();
  const {
    setSelectedProfessional
  } = useAppContext();
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedNurse, setSelectedNurse] = useState<any>(null);
  const [bookingType, setBookingType] = useState('Hourly');
  const [trackingState, setTrackingState] = useState(0);
  const categories = ['Elderly Care', 'Post-surgery Care', 'Injection Services', 'Wound Dressing', 'Maternity Care', 'Home Monitoring'];
  const handleSelectCategory = (cat: string) => {
    setCategory(cat);
    setStep(2);
    setLoading(true);
    setTimeout(() => setLoading(false), 1000);
  };
  const handleSelectNurse = (nurse: any) => {
    setSelectedNurse(nurse);
    setStep(3);
  };
  const handlePayment = () => {
    setStep(5); // Skip to tracking
  };
  useEffect(() => {
    if (step === 5) {
      const t1 = setTimeout(() => setTrackingState(1), 2000);
      const t2 = setTimeout(() => setTrackingState(2), 5000);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [step]);
  const filteredNurses = professionals.filter(p => p.title.includes('Nurse') || p.specialty.includes('Care'));
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-514">
      <div className="bg-white px-6 pt-12 pb-4 shadow-sm sticky top-0 z-10" data-id="element-515">
        <div className="flex items-center gap-3 mb-4" data-id="element-516">
          <button onClick={() => step > 1 && step < 5 ? setStep(step - 1) : navigate(-1)} className="p-2 -ml-2 hover:bg-slate-50 rounded-full" data-id="element-517">
            <ArrowLeft className="w-6 h-6 text-slate-700" data-id="element-518" />
          </button>
          <h1 className="text-xl font-bold text-slate-900" data-id="element-519">Find a Nurse</h1>
        </div>
        {step < 5 && <div className="flex gap-2" data-id="element-520">
            {[1, 2, 3, 4].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-pink-600' : 'bg-slate-200'}`} data-id="element-521" />)}
          </div>}
      </div>

      <div className="p-6 flex-1 overflow-y-auto" data-id="element-522">
        {step === 1 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-523">
            <h2 className="font-semibold text-slate-900 mb-2" data-id="element-524">
              What kind of care do you need?
            </h2>
            <div className="grid grid-cols-2 gap-3" data-id="element-525">
              {categories.map(cat => <button key={cat} onClick={() => handleSelectCategory(cat)} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex flex-col items-center text-center active:scale-95 transition-transform hover:border-pink-200" data-id="element-526">
                  <HeartPulse className="w-8 h-8 text-pink-500 mb-2" data-id="element-527" />
                  <span className="font-medium text-slate-700 text-sm" data-id="element-528">
                    {cat}
                  </span>
                </button>)}
            </div>
          </motion.div>}

        {step === 2 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-529">
            <h2 className="font-semibold text-slate-900 mb-2" data-id="element-530">
              Nurses for {category}
            </h2>
            {loading ? <div className="space-y-4" data-id="element-531">
                {[1, 2].map(i => <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 animate-pulse h-32" data-id="element-532" />)}
              </div> : filteredNurses.map(nurse => <div key={nurse.id} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100" data-id="element-533">
                  <div className="flex space-x-4" data-id="element-534">
                    <img src={nurse.image} alt={nurse.name} className="w-16 h-16 rounded-full object-cover" data-id="element-535" />
                    <div className="flex-1" data-id="element-536">
                      <div className="flex items-center" data-id="element-537">
                        <h3 className="font-bold text-slate-900 text-base" data-id="element-538">
                          {nurse.name}
                        </h3>
                        {nurse.verified && <ShieldCheck size={16} className="text-pink-600 ml-1" data-id="element-539" />}
                      </div>
                      <p className="text-sm text-slate-500" data-id="element-540">
                        {nurse.experience} yrs experience
                      </p>
                      <div className="flex items-center mt-1 space-x-3 text-xs text-slate-500" data-id="element-541">
                        <span className="flex items-center" data-id="element-542">
                          <Star size={12} className="text-amber-400 fill-current mr-1" data-id="element-543" />{' '}
                          {nurse.rating}
                        </span>
                        <span className="flex items-center" data-id="element-544">
                          <MapPin size={12} className="mr-1" data-id="element-545" /> {nurse.distance}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between" data-id="element-546">
                    <div data-id="element-547">
                      <span className="text-xs text-slate-500 block" data-id="element-548">
                        Hourly Rate
                      </span>
                      <span className="text-sm font-bold text-pink-600" data-id="element-549">
                        ₦{nurse.fee.toLocaleString()}/hr
                      </span>
                    </div>
                    <div className="space-x-2" data-id="element-550">
                      <button onClick={() => {
                setSelectedProfessional(nurse);
                navigate(`/profile/${nurse.id}`);
              }} className="text-pink-600 text-sm font-semibold px-3 py-2" data-id="element-551">
                        Profile
                      </button>
                      <button onClick={() => handleSelectNurse(nurse)} className="bg-pink-600 text-white text-sm font-semibold px-5 py-2 rounded-xl active:bg-pink-700" data-id="element-552">
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>)}
          </motion.div>}

        {step === 3 && selectedNurse && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-6" data-id="element-553">
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4" data-id="element-554">
              <img src={selectedNurse.image} className="w-12 h-12 rounded-full object-cover" data-id="element-555" />
              <div data-id="element-556">
                <h3 className="font-bold text-slate-900" data-id="element-557">
                  {selectedNurse.name}
                </h3>
                <p className="text-sm text-slate-500" data-id="element-558">
                  ₦{selectedNurse.fee.toLocaleString()}/hr
                </p>
              </div>
            </div>

            <div data-id="element-559">
              <h3 className="font-semibold text-slate-900 mb-3" data-id="element-560">
                Booking Type
              </h3>
              <div className="space-y-3" data-id="element-561">
                {['Hourly', 'Daily (8 hrs)', 'Overnight (12 hrs)'].map(type => <button key={type} onClick={() => setBookingType(type)} className={`w-full p-4 rounded-xl border-2 text-left transition-colors ${bookingType === type ? 'border-pink-600 bg-pink-50' : 'border-slate-200 bg-white'}`} data-id="element-562">
                      <span className={`font-medium ${bookingType === type ? 'text-pink-700' : 'text-slate-700'}`} data-id="element-563">
                        {type}
                      </span>
                    </button>)}
              </div>
            </div>

            <div data-id="element-564">
              <h3 className="font-semibold text-slate-900 mb-3" data-id="element-565">
                Patient Details
              </h3>
              <textarea className="w-full bg-white border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:border-pink-600 focus:ring-1 focus:ring-pink-600" rows={3} placeholder="Describe the patient's condition..." data-id="element-566" />
              <input type="text" className="w-full bg-white border border-slate-200 rounded-xl p-3 text-sm mt-3 focus:outline-none focus:border-pink-600 focus:ring-1 focus:ring-pink-600" defaultValue="14 Admiralty Way, Lekki Phase 1" data-id="element-567" />
            </div>

            <button onClick={() => setStep(4)} className="w-full bg-pink-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm hover:bg-pink-700" data-id="element-568">
              Continue to Summary
            </button>
          </motion.div>}

        {step === 4 && selectedNurse && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-6" data-id="element-569">
            <h2 className="text-xl font-bold text-slate-900" data-id="element-570">
              Payment Summary
            </h2>
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3" data-id="element-571">
              <div className="flex justify-between text-sm" data-id="element-572">
                <span className="text-slate-500" data-id="element-573">Nurse</span>
                <span className="font-medium" data-id="element-574">{selectedNurse.name}</span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-575">
                <span className="text-slate-500" data-id="element-576">Duration</span>
                <span className="font-medium" data-id="element-577">{bookingType}</span>
              </div>
              <div className="pt-3 border-t border-slate-100 flex justify-between font-bold text-lg" data-id="element-578">
                <span data-id="element-579">Total</span>
                <span className="text-pink-600" data-id="element-580">
                  ₦
                  {(selectedNurse.fee * (bookingType.includes('8') ? 8 : bookingType.includes('12') ? 12 : 2)).toLocaleString()}
                </span>
              </div>
            </div>
            <button onClick={handlePayment} className="w-full bg-pink-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm hover:bg-pink-700" data-id="element-581">
              Pay Securely
            </button>
          </motion.div>}

        {step === 5 && selectedNurse && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-582">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-583">
              <div className="h-48 bg-slate-200 relative" data-id="element-584">
                <img src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=600" className="w-full h-full object-cover opacity-50" data-id="element-585" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-586">
                  <div className="bg-white px-4 py-2 rounded-full shadow-lg font-bold text-pink-600 flex items-center gap-2" data-id="element-587">
                    <Clock className="w-4 h-4 animate-pulse" data-id="element-588" />
                    {trackingState === 0 ? 'Assigning...' : trackingState === 1 ? 'ETA: 15 mins' : 'Arrived'}
                  </div>
                </div>
              </div>
              <div className="p-5" data-id="element-589">
                <div className="flex items-center justify-between mb-6" data-id="element-590">
                  <div className="flex items-center gap-3" data-id="element-591">
                    <img src={selectedNurse.image} className="w-12 h-12 rounded-full object-cover" data-id="element-592" />
                    <div data-id="element-593">
                      <h3 className="font-bold text-slate-900" data-id="element-594">
                        {selectedNurse.name}
                      </h3>
                      <p className="text-xs text-slate-500" data-id="element-595">
                        Your assigned nurse
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-596">
                    <button className="w-10 h-10 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center" data-id="element-597">
                      <MessageCircle className="w-5 h-5" data-id="element-598" />
                    </button>
                    <button className="w-10 h-10 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center" data-id="element-599">
                      <Phone className="w-5 h-5" data-id="element-600" />
                    </button>
                  </div>
                </div>

                <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-slate-200" data-id="element-601">
                  {['Nurse Assigned', 'On the Way', 'Arrived'].map((status, idx) => <div key={status} className="relative flex items-center gap-4" data-id="element-602">
                        <div className={`w-10 h-10 rounded-full border-4 border-white flex items-center justify-center z-10 ${trackingState >= idx ? 'bg-pink-600 text-white' : 'bg-slate-200 text-slate-400'}`} data-id="element-603">
                          <CheckCircle2 className="w-5 h-5" data-id="element-604" />
                        </div>
                        <span className={`font-medium ${trackingState >= idx ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-605">
                          {status}
                        </span>
                      </div>)}
                </div>
              </div>
            </div>
            {trackingState === 2 && <button onClick={() => navigate('/rating')} className="w-full bg-pink-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm" data-id="element-606">
                Rate Service
              </button>}
          </motion.div>}
      </div>
    </div>;
}